import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Droplets, Wrench, Truck, Sparkles, Check, MapPin, Phone,
  Clock, Building2, PaintBucket, Shield, Loader2, Zap, Award, Camera, X, Upload,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { icon: Building2, key: "fleet_recon", title: "Fleet Reconditioning", desc: "End-to-end recon for fleet operators — wash, detail, paint, body, ready-for-resale in days, not weeks.", badge: "Fleet Specialty" },
  { icon: Wrench, key: "bodyshop", title: "Auto Body Shop", desc: "Collision repair, dent removal, panel replacement — insurance-friendly, warranty-backed." },
  { icon: Zap, key: "pdr", title: "Paintless Dent Removal", desc: "Same-day PDR for hail, door dings, and creases — no paint, no filler, factory finish preserved.", badge: "Same-Day" },
  { icon: Award, key: "classic", title: "Classic Cars & Custom Paint", desc: "Restoration-grade paint, panel work, and color-match for classic and custom builds. Booked by appointment.", badge: "Specialty" },
  { icon: Sparkles, key: "detail", title: "Auto Detail", desc: "Showroom-grade interior + exterior detail. Ceramic-ready prep. Odor & stain elimination." },
  { icon: PaintBucket, key: "paint", title: "Paint & Refinish", desc: "Color-matched OEM paint. Full or spot repair. Baked, buffed, delivered." },
  { icon: Droplets, key: "carwash", title: "Express Carwash", desc: "High-volume hand wash + spray. Perfect for lot maintenance." },
  { icon: Truck, key: "porter", title: "Porter Service", desc: "Vehicle transport, lot moves, dealer swaps — insured drivers." },
];

const SERVICE_AREA = ["Wilmington", "San Pedro", "Carson", "Long Beach", "Harbor City", "Torrance"];

const WHY = [
  { icon: Zap, title: "Fast Turnaround", desc: "Same-week delivery on most jobs. Rush jobs available." },
  { icon: Award, title: "High Quality", desc: "Certified technicians, factory-grade paint, inspection at every step." },
  { icon: Shield, title: "Insurance Friendly", desc: "We work directly with all major carriers on body repairs." },
  { icon: Truck, title: "Fleet First", desc: "Volume pricing, dedicated account manager, single invoice." },
];

const HERO_IMG = "https://images.unsplash.com/photo-1632081831947-24ffdea2cd04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxkZWFsZXJzaGlwJTIwY2Fyc3xlbnwwfHx8fDE3NzgwNDI0NTR8MA&ixlib=rb-4.1.0&q=85";

const EMPTY_FORM = {
  service_type: "fleet_recon", name: "", email: "", phone: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  vehicle_count: "", company: "", damage_description: "",
  insurance_involved: false, notes: "", photos: [],
};

// Client-side JPEG compressor — max 1600px wide, quality 0.7, base64 data URL.
function compressImage(file, maxWidth = 1600, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function EstimateForm({ initialService = "fleet_recon", compact = false }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, service_type: initialService });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);

  const addPhotos = async (files) => {
    if (!files || files.length === 0) return;
    setPhotoBusy(true); setError("");
    try {
      const remaining = 3 - form.photos.length;
      const toProcess = Array.from(files).slice(0, remaining);
      const compressed = await Promise.all(toProcess.map((f) => compressImage(f)));
      setForm((s) => ({ ...s, photos: [...s.photos, ...compressed] }));
    } catch (e) {
      setError("Couldn't read one of the photos. Try a different image.");
    } finally { setPhotoBusy(false); }
  };

  const removePhoto = (i) => {
    setForm((s) => ({ ...s, photos: s.photos.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...form };
      if (payload.vehicle_year === "") delete payload.vehicle_year;
      else payload.vehicle_year = parseInt(payload.vehicle_year);
      if (payload.vehicle_count === "") delete payload.vehicle_count;
      else payload.vehicle_count = parseInt(payload.vehicle_count);
      if (payload.photos.length === 0) delete payload.photos;
      for (const k of Object.keys(payload)) if (payload[k] === "") delete payload[k];
      const { data } = await axios.post(`${API}/estimates`, payload);
      setSuccess(data.id);
      setForm({ ...EMPTY_FORM, service_type: initialService });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed. Please try again or call us.");
    } finally { setBusy(false); }
  };

  const isBodyshop = ["bodyshop", "pdr", "classic"].includes(form.service_type);
  const isFleet = form.service_type === "fleet_recon";
  const showPhotos = ["bodyshop", "pdr", "paint", "classic", "detail"].includes(form.service_type);

  if (success) {
    return (
      <div className="border-2 border-black p-8 bg-white text-center" data-testid="estimate-success">
        <Check size={40} className="mx-auto text-[#FF2B00]" />
        <h3 className="font-display text-3xl tracking-tighter mt-4">Estimate Request Received</h3>
        <p className="text-zinc-600 mt-3">We'll reach out within the same business day. Your confirmation ID:</p>
        <p className="mt-2 font-mono text-xs text-zinc-500">{success}</p>
        <button
          onClick={() => setSuccess(null)}
          data-testid="estimate-new-btn"
          className="mt-6 border-2 border-black px-5 py-3 hover:bg-black hover:text-white label-caps"
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black p-6 lg:p-8 space-y-4" data-testid="estimate-form">
      <div>
        <label className="label-caps block mb-2">Service Needed</label>
        <select
          data-testid="est-service-type"
          value={form.service_type}
          onChange={(e) => setForm({ ...form, service_type: e.target.value })}
          className="w-full border border-black px-3 py-3 bg-white"
        >
          <option value="fleet_recon">Fleet Reconditioning (Multiple Vehicles)</option>
          <option value="bodyshop">Auto Body Shop / Collision Repair</option>
          <option value="pdr">Paintless Dent Removal (PDR)</option>
          <option value="classic">Classic Car / Custom Paint</option>
          <option value="detail">Auto Detail</option>
          <option value="paint">Paint / Refinish</option>
          <option value="carwash">Carwash</option>
          <option value="porter">Porter / Transport</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps block mb-2">Your Name *</label>
          <input data-testid="est-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-black px-3 py-3" />
        </div>
        <div>
          <label className="label-caps block mb-2">Phone *</label>
          <input data-testid="est-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-black px-3 py-3" />
        </div>
      </div>

      <div>
        <label className="label-caps block mb-2">Email *</label>
        <input data-testid="est-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-black px-3 py-3" />
      </div>

      {isFleet ? (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-caps block mb-2">Company</label>
            <input data-testid="est-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-black px-3 py-3" />
          </div>
          <div>
            <label className="label-caps block mb-2">Fleet Size</label>
            <input data-testid="est-fleet-size" type="number" min="1" value={form.vehicle_count} onChange={(e) => setForm({ ...form, vehicle_count: e.target.value })} className="w-full border border-black px-3 py-3" placeholder="# vehicles" />
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-caps block mb-2">Year</label>
            <input data-testid="est-year" type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} className="w-full border border-black px-3 py-3" />
          </div>
          <div>
            <label className="label-caps block mb-2">Make</label>
            <input data-testid="est-make" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} className="w-full border border-black px-3 py-3" />
          </div>
          <div>
            <label className="label-caps block mb-2">Model</label>
            <input data-testid="est-model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="w-full border border-black px-3 py-3" />
          </div>
        </div>
      )}

      {isBodyshop && (
        <>
          <div>
            <label className="label-caps block mb-2">Damage Description</label>
            <textarea data-testid="est-damage" rows={3} value={form.damage_description} onChange={(e) => setForm({ ...form, damage_description: e.target.value })} className="w-full border border-black px-3 py-3" placeholder="Describe the damage and location on the vehicle" />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="est-insurance" id="ins" type="checkbox" checked={form.insurance_involved} onChange={(e) => setForm({ ...form, insurance_involved: e.target.checked })} className="w-4 h-4" />
            <label htmlFor="ins" className="text-sm">Insurance claim involved</label>
          </div>
        </>
      )}

      {showPhotos && (
        <div>
          <label className="label-caps block mb-2 flex items-center gap-2">
            <Camera size={14} /> Photos of the vehicle / damage (up to 3, optional)
          </label>
          <div className="flex flex-wrap gap-3">
            {form.photos.map((p, i) => (
              <div key={i} className="relative w-24 h-24 border-2 border-black" data-testid={`est-photo-${i}`}>
                <img src={p} alt={`upload ${i}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-black text-white w-6 h-6 flex items-center justify-center hover:bg-[#FF2B00]" data-testid={`est-photo-remove-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
            {form.photos.length < 3 && (
              <label className="w-24 h-24 border-2 border-dashed border-black flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-50 text-xs text-zinc-500 gap-1">
                {photoBusy ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} /><span>Add</span></>}
                <input data-testid="est-photo-upload" type="file" accept="image/*" capture="environment" multiple onChange={(e) => addPhotos(e.target.files)} className="hidden" />
              </label>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-2">Tip: photos speed up your estimate — snap them from your phone.</p>
        </div>
      )}

      <div>
        <label className="label-caps block mb-2">Additional Notes</label>
        <textarea data-testid="est-notes" rows={compact ? 2 : 3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-3" placeholder="Anything else we should know" />
      </div>

      {error && <div className="text-sm text-[#FF2B00]" data-testid="estimate-error">{error}</div>}

      <button
        data-testid="estimate-submit"
        type="submit"
        disabled={busy}
        className="w-full bg-black text-white py-4 hover:bg-[#FF2B00] disabled:opacity-50 label-caps inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="animate-spin" size={16} /> : <>Request My Free Estimate <ArrowRight size={16} /></>}
      </button>
      <p className="text-xs text-zinc-500 text-center">No obligation. Same-day response during business hours.</p>
    </form>
  );
}

export default function Landing() {
  const [biz, setBiz] = useState({
    app_name: "EAG Solutions Inc.",
    tagline: "Fast Turnaround. High Quality.",
    phone: "(213) 494-7421",
    address: "1757 East O St, Wilmington, CA 90744",
    work_hours: "Mon-Fri 8am-6pm",
  });

  useEffect(() => {
    axios.get(`${API}/public/business`).then(({ data }) => setBiz({ ...biz, ...data })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phoneHref = "tel:" + biz.phone.replace(/[^0-9+]/g, "");

  return (
    <div className="min-h-screen bg-[#F4F4F5] text-black">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1.5" data-testid="nav-logo">
            <span className="font-display text-2xl tracking-tighter">{biz.app_name}</span>
            <span className="label-caps text-[#FF2B00]">●</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 label-caps">
            <a href="#services" className="hover:text-[#FF2B00]">Services</a>
            <a href="#fleet" className="hover:text-[#FF2B00]">Fleet</a>
            <a href="#photo-estimate" className="hover:text-[#FF2B00]">Get Estimate</a>
            <a href="#contact" className="hover:text-[#FF2B00]">Contact</a>
          </div>
          <div className="flex items-center gap-3">
            <a href={phoneHref} data-testid="nav-phone" className="hidden sm:inline label-caps text-black hover:text-[#FF2B00]">
              {biz.phone}
            </a>
            <a href="#photo-estimate" data-testid="nav-cta" className="bg-[#FF2B00] text-white px-4 py-2 hover:bg-black label-caps inline-flex items-center gap-2">
              <Camera size={14} /> Photo Estimate
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="grid-pattern border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="label-caps text-[#FF2B00] mb-4">Wilmington, CA ● Detail ● Body Shop ● Fleet Recon</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              {biz.tagline.split(".")[0]}. <span className="text-[#FF2B00]">{biz.tagline.split(".")[1]?.trim() || "High Quality"}.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              {biz.app_name} is the Los Angeles-area specialist in <strong>fleet reconditioning</strong>, <strong>auto body repair</strong>, and <strong>full-service detail</strong>.
              Trusted by dealerships, rental companies, and fleet operators for volume work done right the first time.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#photo-estimate" data-testid="hero-cta-estimate" className="bg-[#FF2B00] text-white px-6 py-4 hover:bg-black label-caps inline-flex items-center gap-2 shadow-lg">
                <Camera size={16} /> Free Photo Estimate in 5 Minutes
              </a>
              <a href={phoneHref} data-testid="hero-cta-call" className="border-2 border-black px-6 py-4 hover:bg-black hover:text-white label-caps inline-flex items-center gap-2">
                <Phone size={14} /> {biz.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-zinc-500 label-caps">
              <span className="flex items-center gap-2"><Check size={12} className="text-[#FF2B00]" /> All major insurance</span>
              <span className="flex items-center gap-2"><Check size={12} className="text-[#FF2B00]" /> Same-day PDR</span>
              <span className="flex items-center gap-2"><Check size={12} className="text-[#FF2B00]" /> Certified techs</span>
            </div>
            <div className="mt-6 label-caps text-xs text-zinc-500">
              Serving: {SERVICE_AREA.join(" · ")}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-black -rotate-1"></div>
            <img src={HERO_IMG} alt="EAG Solutions - dealership vehicles" className="relative w-full h-[440px] object-cover" />
            <div className="relative bg-white border-2 border-black p-5 mt-[-60px] mx-4 z-10">
              <div className="label-caps text-[#FF2B00] mb-1">Fleet Managers</div>
              <p className="text-sm">Volume pricing, single invoice, dedicated account manager. Same-week delivery on standard recon.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Badge Banner */}
      <section className="bg-[#FF2B00] text-white border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-3 text-center">
          <Shield size={20} className="shrink-0" />
          <div className="font-display text-lg sm:text-2xl tracking-tight" data-testid="insurance-badge">
            We Work With All Major Insurance Companies
          </div>
          <div className="label-caps text-xs opacity-90 border-l border-white/40 pl-3 ml-2 hidden sm:inline">
            Direct billing · Fast claims · No hassle
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {WHY.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="bg-white p-8" data-testid={`why-${i}`}>
                <Icon size={22} className="text-[#FF2B00]" />
                <h3 className="font-display text-xl mt-4">{title}</h3>
                <p className="text-sm text-zinc-600 mt-2">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-caps text-[#FF2B00]">01 ● Services</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Full-stack auto recon.</h2>
            </div>
            <p className="text-zinc-600 max-w-md">Every service under one roof — wash to full collision repair. Consistent quality across every vehicle in your fleet.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {SERVICES.map(({ icon: Icon, key, title, desc, badge }, i) => (
              <div key={key} className="bg-white p-8 hover:bg-zinc-50 group cursor-default relative" data-testid={`service-card-${i}`}>
                {badge && <div className="absolute top-4 right-4 label-caps text-[10px] bg-[#FF2B00] text-white px-2 py-1">{badge}</div>}
                <Icon size={28} className="text-[#FF2B00]" />
                <h3 className="font-display text-2xl mt-6">{title}</h3>
                <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{desc}</p>
                <a href="#photo-estimate" className="mt-6 label-caps text-zinc-400 group-hover:text-black inline-flex items-center gap-2">
                  Get estimate <ArrowRight size={12} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet CTA */}
      <section id="fleet" className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-caps text-[#FF2B00]">02 ● Fleet Specialty</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3 leading-none">Run a fleet? <br /><span className="text-[#FF2B00]">We're your one call.</span></h2>
            <ul className="mt-8 space-y-3 text-zinc-300">
              <li className="flex gap-3"><Check size={18} className="text-[#FF2B00] mt-0.5 shrink-0" /> Wash → detail → paint → body → delivery, all in-house</li>
              <li className="flex gap-3"><Check size={18} className="text-[#FF2B00] mt-0.5 shrink-0" /> Volume pricing based on quarterly commitment</li>
              <li className="flex gap-3"><Check size={18} className="text-[#FF2B00] mt-0.5 shrink-0" /> Dedicated account manager + single monthly invoice</li>
              <li className="flex gap-3"><Check size={18} className="text-[#FF2B00] mt-0.5 shrink-0" /> Pickup + delivery within the LA basin</li>
            </ul>
            <a href="#photo-estimate" className="mt-10 inline-flex items-center gap-2 bg-[#FF2B00] text-white px-6 py-4 hover:bg-white hover:text-black label-caps">
              Get Fleet Quote <ArrowRight size={16} />
            </a>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="label-caps text-[#FF2B00] mb-4">Typical Fleet Package</div>
            <div className="space-y-3 text-sm">
              {[
                ["Exterior wash + interior vacuum", "Every 30 days"],
                ["Full detail + odor treatment", "Turnover-based"],
                ["Body repair + paint touch-up", "As needed"],
                ["Fleet reconditioning (resale prep)", "Priority queue"],
              ].map(([label, cadence]) => (
                <div key={label} className="flex justify-between border-b border-zinc-800 pb-2">
                  <span>{label}</span>
                  <span className="text-zinc-400 label-caps text-xs">{cadence}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Estimate form */}
      <section id="photo-estimate" className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-caps text-[#FF2B00]">03 ● 5-Minute Photo Estimate</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Accident or dent? <br />Get a quote today.</h2>
            <p className="mt-6 text-zinc-600 max-w-md">Snap 2–3 photos of the damage from your phone — we'll respond with an honest estimate the same business day.</p>
            <div className="mt-6 flex items-center gap-2 bg-white border-2 border-black px-4 py-3">
              <Shield size={20} className="text-[#FF2B00] shrink-0" />
              <div>
                <div className="label-caps text-xs text-[#FF2B00]">Insurance claim?</div>
                <div className="text-sm">We work with all major carriers directly.</div>
              </div>
            </div>
            <div className="mt-6 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-[#FF2B00] mt-1" />
                <div>
                  <div className="label-caps text-zinc-500">Direct line</div>
                  <a href={phoneHref} className="font-display text-xl hover:text-[#FF2B00]" data-testid="contact-phone">{biz.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#FF2B00] mt-1" />
                <div>
                  <div className="label-caps text-zinc-500">Shop</div>
                  <div>{biz.address}</div>
                  <div className="label-caps text-xs text-zinc-500 mt-1">Serving {SERVICE_AREA.join(" · ")}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-[#FF2B00] mt-1" />
                <div>
                  <div className="label-caps text-zinc-500">Hours</div>
                  <div>{biz.work_hours}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <EstimateForm initialService="bodyshop" />
          </div>
        </div>
      </section>

      {/* Contact / Footer */}
      <footer id="contact" className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-display text-2xl">{biz.app_name} <span className="text-[#FF2B00]">●</span></div>
            <p className="mt-3 text-sm text-zinc-400">Detail. Body. Fleet reconditioning. Wilmington, CA.</p>
          </div>
          <div>
            <div className="label-caps text-zinc-500 mb-3">Visit</div>
            <div className="text-sm">{biz.address}</div>
            <div className="text-sm mt-1">{biz.work_hours}</div>
          </div>
          <div>
            <div className="label-caps text-zinc-500 mb-3">Contact</div>
            <a href={phoneHref} className="text-sm hover:text-[#FF2B00] block">{biz.phone}</a>
            <a href="#photo-estimate" className="text-sm hover:text-[#FF2B00] block mt-1">Request an estimate</a>
            <Link to="/login" className="text-sm text-zinc-500 hover:text-white block mt-4 label-caps">Staff Sign In</Link>
          </div>
        </div>
        <div className="border-t border-zinc-800 px-6 py-6 text-center">
          <div className="label-caps text-zinc-500 text-xs">© {new Date().getFullYear()} {biz.app_name} — All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
